import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import { Save as SaveIcon, Cancel as CancelIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { sekurantenProjekteAPI, projectsAPI } from '../../services/api';
import { SekurantenProjekt, Project } from '../../types/api';
import { SignaturePad } from '../../components/SignaturePad/SignaturePad';

export const SekurantenProjektForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const isEdit = !!id;

  // Rollen-basierte Berechtigungen für Sekuranten
  const isAdmin = user?.role === 'admin';
  const isManagement = user?.role === 'management';
  const isBauleiter = user?.role === 'bauleiter';
  const isForeman = user?.role === 'foreman';
  const isInstaller = user?.role === 'installer';
  
  // Admin, Management & Bauleiter können alles
  const canSeeAll = isAdmin || isManagement || isBauleiter;
  // Vorarbeiter & Monteure haben eingeschränkte Rechte
  const canSeeRestricted = isForeman || isInstaller;
  
  // Sekuranten-Berechtigungen:
  const canCreateSekuranten = canSeeAll || canSeeRestricted; // Alle können erstellen
  const canEditSekuranten = canSeeAll; // Nur Admin, Management, Bauleiter können alle bearbeiten

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [projects, setProjects] = useState<Project[]>([]);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    project_id: null as number | null,
    objektname: '',
    objektadresse: '',
    montagefirma_name: 'Rooftop GmbH',
    montagefirma_adresse: 'Siegelsdorferstrasse 31, 90768 Burgfarrnbach',
  });
  const [signature, setSignature] = useState<string>('');
  const [unterschriftName, setUnterschriftName] = useState('');

  useEffect(() => {
    // Berechtigung prüfen
    if (isEdit && !canEditSekuranten) {
      enqueueSnackbar('Keine Berechtigung zum Bearbeiten von Sekuranten-Projekten', { variant: 'error' });
      navigate('/sekuranten');
      return;
    }
    if (!isEdit && !canCreateSekuranten) {
      enqueueSnackbar('Keine Berechtigung zum Erstellen von Sekuranten-Projekten', { variant: 'error' });
      navigate('/sekuranten');
      return;
    }

    fetchProjects();
    if (isEdit) {
      fetchProjekt();
    }
  }, [id, isEdit, canEditSekuranten, canCreateSekuranten, navigate, enqueueSnackbar]);

  const fetchProjects = async () => {
    try {
      const response = await projectsAPI.getProjects();
      setProjects(response);
    } catch (error) {
      console.error('Error fetching projects:', error);
      enqueueSnackbar('Fehler beim Laden der Projekte', { variant: 'error' });
    }
  };

  const fetchProjekt = async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      const response = await sekurantenProjekteAPI.getProjekt(parseInt(id));
      setFormData({
        name: response.name,
        description: response.description || '',
        project_id: response.project_id || null,
        objektname: response.objektname || '',
        objektadresse: response.objektadresse || '',
        montagefirma_name: response.montagefirma_name || 'Rooftop GmbH',
        montagefirma_adresse: response.montagefirma_adresse || 'Siegelsdorferstrasse 31, 90768 Burgfarrnbach',
      });
    } catch (error) {
      console.error('Error fetching projekt:', error);
      enqueueSnackbar('Fehler beim Laden des Sekuranten-Projekts', { variant: 'error' });
      navigate('/sekuranten');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      enqueueSnackbar('Bitte geben Sie einen Namen ein', { variant: 'warning' });
      return;
    }
    if (!signature) {
      enqueueSnackbar('Bitte geben Sie eine Unterschrift ein', { variant: 'warning' });
      return;
    }
    if (!unterschriftName.trim()) {
      enqueueSnackbar('Bitte geben Sie den Namen der unterschreibenden Person ein', { variant: 'warning' });
      return;
    }
    try {
      setSaving(true);
      const form = new FormData();
      form.append('name', formData.name);
      form.append('description', formData.description || '');
      if (formData.project_id) form.append('project_id', String(formData.project_id));
      form.append('objektname', formData.objektname || '');
      form.append('objektadresse', formData.objektadresse || '');
      form.append('montagefirma_name', formData.montagefirma_name);
      form.append('montagefirma_adresse', formData.montagefirma_adresse);
      form.append('unterschrift_name', unterschriftName);
      // Signature als PNG Blob anhängen
      const res = await fetch(signature);
      const blob = await res.blob();
      form.append('signature', blob, 'signature.png');
      if (isEdit) {
        await sekurantenProjekteAPI.updateProjekt(parseInt(id!), form);
        enqueueSnackbar('Sekuranten-Projekt erfolgreich aktualisiert', { variant: 'success' });
      } else {
        await sekurantenProjekteAPI.createProjekt(form);
        enqueueSnackbar('Sekuranten-Projekt erfolgreich erstellt', { variant: 'success' });
      }
      navigate('/sekuranten');
    } catch (error) {
      console.error('Error saving projekt:', error);
      enqueueSnackbar('Fehler beim Speichern des Sekuranten-Projekts', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/sekuranten');
  };

  const handleProjectChange = (e: any) => {
    const selectedId = e.target.value ? Number(e.target.value) : null;
    let objektname = formData.objektname;
    let objektadresse = formData.objektadresse;
    if (selectedId) {
      const selectedProject = projects.find((p) => p.id === selectedId);
      if (selectedProject) {
        objektname = selectedProject.description || '';
        objektadresse = selectedProject.site_address || '';
      }
    } else {
      objektname = '';
      objektadresse = '';
    }
    setFormData({
      ...formData,
      project_id: selectedId,
      objektname,
      objektadresse,
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" component="h1" gutterBottom>
        {isEdit ? 'Sekuranten-Projekt bearbeiten' : 'Neues Sekuranten-Projekt'}
      </Typography>

      <Card>
        <CardContent>
          <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              label="Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              fullWidth
              autoFocus
            />

            <TextField
              label="Beschreibung"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              multiline
              rows={3}
              fullWidth
            />

            <FormControl fullWidth>
              <InputLabel>Verknüpftes Projekt (optional)</InputLabel>
              <Select
                value={formData.project_id || ''}
                onChange={handleProjectChange}
                label="Verknüpftes Projekt (optional)"
              >
                <MenuItem value="">
                  <em>Kein Projekt</em>
                </MenuItem>
                {projects.map((project) => (
                  <MenuItem key={project.id} value={project.id}>
                    {project.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Objektname"
              value={formData.objektname}
              onChange={(e) => setFormData({ ...formData, objektname: e.target.value })}
              fullWidth
              sx={{ mt: 2 }}
            />

            <TextField
              label="Objektadresse"
              value={formData.objektadresse}
              onChange={(e) => setFormData({ ...formData, objektadresse: e.target.value })}
              fullWidth
              sx={{ mt: 2 }}
            />

            <TextField
              label="Name der Montagefirma"
              value={formData.montagefirma_name}
              InputProps={{ readOnly: true }}
              fullWidth
              sx={{ mt: 2 }}
            />

            <TextField
              label="Adresse der Montagefirma"
              value={formData.montagefirma_adresse}
              InputProps={{ readOnly: true }}
              fullWidth
              sx={{ mt: 2 }}
            />

            <TextField
              label="Name der unterschreibenden Person"
              value={unterschriftName}
              onChange={(e) => setUnterschriftName(e.target.value)}
              required
              fullWidth
            />

            <SignaturePad value={signature} onChange={setSignature} />

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={handleCancel}
                disabled={saving}
              >
                Abbrechen
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={saving}
              >
                {saving ? 'Speichern...' : 'Speichern'}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}; 